<template>
  <div class="strategy-grid" :class="{ 'read-only': readOnly }">
    <!-- Controles mínimos -->
    <div
      v-if="!readOnly"
      class="d-flex justify-end align-center mb-16"
      style="margin-top: -85px"
    >
      <div class="controls-right d-flex align-center" style="gap: 10px;">
        <v-btn color="secondary" small rounded @click="openTemplatesDialog">
          <v-icon size="20">mdi-plus</v-icon>
          {{ $t("add_rule") }}
        </v-btn>
      </div>
    </div>

    <!-- Grid principal -->
    <div class="grid-container" ref="gridContainer" :key="componentKey">
      <!-- Mensaje de ayuda durante selección -->
      <div v-if="isSelectingCell && !readOnly" class="selection-help">
        <div class="help-content">
          <v-icon color="primary" size="small">mdi-cursor-pointer</v-icon>
          <span v-if="!rangeSelection.isSelectingRange">
            <template v-if="selectedRuleToAdd && selectedRuleToAdd.isCopied">
              {{ $t("grid_selection_help_paste") }}
              "{{ selectedRuleName }}"
            </template>
            <template v-else>
              {{ $t("grid_selection_help_start") }}
              "{{ selectedRuleName }}"
            </template>
          </span>
          <span v-else>
            {{ $t("grid_selection_help_end") }}
          </span>
          <v-btn
            color="error"
            rounded
            depressed
            @click="handleRuleSelectionCancelled"
          >
            <v-icon size="small">mdi-close</v-icon>
            {{ $t("grid_selection_cancel") }}
          </v-btn>
        </div>
      </div>

      <!-- Encabezado con slots de anticipación -->
      <a-pricing-strategy-grid-rules-grid-header
        :anticipation-slots="anticipationSlots"
        :is-dragging="isDragging || isResizing"
        :highlighted-column-indices="highlightedColumnIndices"
      />

      <a-pricing-strategy-grid-rules-coverage
        :anticipation-slots="anticipationSlots"
        :coverage="slotCoverage"
      />

      <!-- Filas de la grid -->
      <div
        v-for="row in gridRows"
        :key="row.id"
        class="grid-row"
        :class="{
          'drag-over': hoveredRowId === row.id
        }"
        :data-row="row.id"
      >
        <div
          class="cards-container"
          :class="{
            'selecting-cells': isSelectingCell,
            'drag-target': hoveredRowId === row.id
          }"
        >
          <!-- Celdas disponibles para colocar regla -->
          <template v-if="isSelectingCell && !readOnly">
            <div
              v-for="cell in getAvailableCellsForRow(row.id)"
              :key="`cell-${row.id}-${cell.left}`"
              class="available-cell"
              :class="{
                'start-selected':
                  rangeSelection.startCell &&
                  rangeSelection.startCell.rowId === row.id &&
                  rangeSelection.startCell.left === cell.left,
                'in-range': isInDynamicRange(cell, row.id),
                'end-hovered':
                  rangeSelection.isSelectingRange &&
                  rangeSelection.hoveredCell &&
                  rangeSelection.hoveredCell.rowId === row.id &&
                  rangeSelection.hoveredCell.left === cell.left
              }"
              :style="{
                left: cell.left + '%',
                width: cell.width + '%'
              }"
              @click.stop="handleCellClick(cell, row.id)"
              @mouseenter="handleCellHover(cell, row.id)"
              @mouseleave="handleCellLeave()"
            >
              <v-icon
                v-if="!rangeSelection.startCell"
                color="primary"
                class="cell-icon"
              >
                mdi-plus
              </v-icon>
              <v-icon
                v-else-if="
                  rangeSelection.startCell &&
                    rangeSelection.startCell.rowId === row.id &&
                    rangeSelection.startCell.left === cell.left
                "
                color="success"
                class="cell-icon"
              >
                mdi-flag-checkered
              </v-icon>
              <v-icon
                v-else-if="
                  rangeSelection.isSelectingRange &&
                    rangeSelection.hoveredCell &&
                    rangeSelection.hoveredCell.rowId === row.id &&
                    rangeSelection.hoveredCell.left === cell.left
                "
                color="error"
                class="cell-icon"
              >
                mdi-flag
              </v-icon>
              <v-icon
                v-else-if="isInDynamicRange(cell, row.id)"
                color="warning"
                class="cell-icon"
              >
                mdi-minus
              </v-icon>
              <v-icon v-else color="primary" class="cell-icon">
                mdi-plus
              </v-icon>
            </div>
          </template>

          <!-- Cards existentes -->
          <a-pricing-strategy-card-strategy-rule
            v-for="card in row.cards"
            :key="card.id"
            :card="card"
            :row-id="row.id"
            :read-only="readOnly"
            :is-dragging="draggingCardId === card.id"
            :is-resizing="resizingCardId === card.id"
            :drag-transform="dragTransforms.get(card.id) || ''"
            :can-resize-left="canResizeLeft(card, row)"
            :can-resize-right="canResizeRight(card, row)"
            @start-drag="startCardDrag"
            @start-resize-left="startResizeLeft"
            @start-resize-right="startResizeRight"
            @toggle-menu="toggleCardMenu"
            @copy-card="copyCard($event)"
            @edit-card="editCard($event)"
            @delete-card="deleteCard($event, row.id)"
          />
        </div>

        <!-- Controles de fila -->
        <div
          v-if="!readOnly"
          class="row-controls d-flex justify-center align-center flex-column"
        >
          <v-btn
            v-if="row.cards.length > 0"
            @click="clearRowCards(row.id)"
            color="grey"
            icon
            small
            :title="$t('grid_row_controls_clear_all', { label: row.label })"
          >
            <v-icon size="small">mdi-broom</v-icon>
          </v-btn>
          <v-btn
            @click="deleteRow(row.id)"
            color="grey"
            icon
            small
            :disabled="gridRows.length <= 1"
            :title="$t('grid_row_controls_delete', { label: row.label })"
          >
            <v-icon size="small">mdi-delete</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Drop placeholder -->
      <div id="dropPlaceholder" class="drop-placeholder" ref="dropPlaceholder">
        <span>{{ $t("grid_drop_here") }}</span>
      </div>
    </div>

    <!-- Controles inferiores -->
    <a-pricing-strategy-grid-rules-add-row-button
      v-if="!readOnly"
      @add-row="addNewRow"
    />

    <!-- Diálogos de confirmación y edición -->
    <!-- Diálogo para confirmar vaciar fila -->
    <v-dialog
      v-model="dialogs.confirmClearRow"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="500"
    >
      <v-card>
        <v-card-title class="headline justify-center">
          <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
          {{ $t("grid_dialog_clear_row_title") }}
        </v-card-title>
        <v-card-text>
          <div
            v-html="
              $t('grid_dialog_clear_row_message', {
                label: dialogData.targetRowLabel
              })
            "
            class="mb-4"
          ></div>
          <span class="text-caption text-grey">
            {{ $t("grid_dialog_clear_row_warning") }}
          </span>
        </v-card-text>
        <v-card-actions class="ps-5 pb-5 justify-center">
          <v-btn
            color="secondary"
            class="pl-5 pr-5"
            rounded
            @click="confirmClearRowCards"
          >
            {{ $t("empty") }}
          </v-btn>
          <v-btn
            color=""
            text
            rounded
            depressed
            class="pl-5 pr-5"
            @click="dialogs.confirmClearRow = false"
          >
            {{ $t("cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Diálogo para confirmar eliminar fila -->
    <v-dialog v-model="dialogs.confirmDeleteRow" max-width="500">
      <v-card>
        <v-card-title class="headline justify-center">
          <v-icon color="error" class="mr-2">mdi-delete</v-icon>
          {{ $t("grid_dialog_delete_row_title") }}
        </v-card-title>
        <v-card-text>
          <div
            v-html="
              $t('grid_dialog_delete_row_message', {
                label: dialogData.targetRowLabel
              })
            "
            class="mb-4"
          ></div>
          <span class="text-caption text-grey">
            {{ $t("grid_dialog_delete_row_warning") }}
          </span>
        </v-card-text>

        <v-card-actions class="ps-5 pb-5 justify-center">
          <v-btn
            color="error"
            class="pl-5 pr-5"
            rounded
            @click="confirmDeleteRow"
          >
            {{ $t("delete") }}
          </v-btn>
          <v-btn
            color=""
            text
            rounded
            depressed
            class="pl-5 pr-5"
            @click="dialogs.confirmDeleteRow = false"
          >
            {{ $t("cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import aPricingStrategyCardStrategyRule from "@/components/pricing-strategy/aPricingStrategyCardStrategyRule.vue";
import aPricingStrategyGridRulesAddRowButton from "@/components/pricing-strategy/aPricingStrategyGridRulesAddRowButton.vue";
import aPricingStrategyGridRulesCoverage from "@/components/pricing-strategy/aPricingStrategyGridRulesCoverage.vue";
import aPricingStrategyGridRulesGridHeader from "@/components/pricing-strategy/aPricingStrategyGridRulesGridHeader.vue";
import { mapGetters, mapState } from "vuex";

export default {
  name: "a-pricing-strategy-grid-rules",
  components: {
    aPricingStrategyGridRulesAddRowButton,
    aPricingStrategyGridRulesCoverage,
    aPricingStrategyGridRulesGridHeader,
    aPricingStrategyCardStrategyRule
  },
  props: {
    strategyRulesData: {
      type: Object,
      required: true
    },
    anticipationSlots: {
      type: Array,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    // Estado del grid
    gridRows: [],
    componentKey: 0,
    tempCardCounter: 1, // Counter para cards temporales
    copyCardCounter: 1, // Counter para cards copiadas
    tempRowCounter: -1, // Counter para IDs temporales negativos

    // Estado de drag & drop
    isDragging: false,
    isResizing: false,
    currentCard: null,
    currentRow: null,
    dragOffset: { x: 0, y: 0 },
    originalPosition: { left: 0, width: 0 },
    dragStartPosition: { x: 0, y: 0 },
    resizeStartX: 0,
    resizeStartWidth: 0,
    resizeStartLeft: 0,
    resizeSide: null,
    activeMenu: null,
    highlightedColumnIndices: [],

    // Estados reactivos para clases CSS
    hoveredRowId: null,
    draggingCardId: null,
    resizingCardId: null,
    dragTransforms: new Map(),

    // Estado para selección de reglas
    selectedRuleToAdd: null,
    isSelectingCell: false,
    availableCells: [],
    rangeSelection: {
      isSelectingRange: false,
      startCell: null,
      endCell: null,
      targetRowId: null,
      hoveredCell: null
    },

    // Estado para copy/paste de reglas
    copiedCard: null,
    selectedCardForCopy: null,

    // Estado para diálogos
    dialogs: {
      confirmClearRow: false,
      confirmDeleteRow: false
    },
    dialogData: {
      targetRowId: null,
      targetRowLabel: "",
      targetCardId: null,
      newCardTitle: ""
    }
  }),
  computed: {
    // Computadas de la store ------------------------------------------------------------
    rule_definition_dialog: {
      get() {
        return this.$store.state.pricingstrategy.rule_definition_dialog;
      },
      set(value) {
        this.$store.dispatch("setPricingStrategyRuleDefinitionDialog", value);
      }
    },
    rule_to_edit: {
      get() {
        return this.$store.state.pricingstrategy.rule_to_edit;
      },
      set(value) {
        this.$store.dispatch("setPricingStrategyRuleToEdit", value);
      }
    },
    gridColumns() {
      return this.anticipationSlots.length;
    },
    slotCoverage() {
      const n = this.anticipationSlots.length;
      const slotWidth = 100 / n;
      const result = Array.from({ length: n }, () => []);
      this.gridRows.forEach(row => {
        row.cards.forEach(card => {
          const start = Math.round(card.left / slotWidth);
          const span = Math.max(1, Math.round(card.width / slotWidth));
          for (let i = Math.max(0, start); i < start + span && i < n; i++) {
            if (!result[i].some(e => e.ruleId === card.ruleId)) {
              result[i].push({ ruleId: card.ruleId, color: card.rule.color });
            }
          }
        });
      });
      return result;
    },
    reversedAnticipationSlots() {
      return [...this.anticipationSlots];
    },
    // Nombre de la regla seleccionada, tanto si viene del menú de reglas
    // como de una tarjeta copiada (rule anidada)
    selectedRuleName() {
      if (!this.selectedRuleToAdd) return "";
      const rule = this.selectedRuleToAdd.rule || this.selectedRuleToAdd;
      return rule.name || "";
    },
    processedStrategyData() {
      const processedRows = [];

      // Obtener todas las filas únicas ordenadas por position (sin mutar el original)
      const uniqueRows = [...this.strategyRulesData.strategyRows].sort(
        (a, b) => a.position - b.position
      );

      uniqueRows.forEach(strategyRow => {
        const processedCards = [];

        // Procesar cada regla en la fila
        strategyRow.strategyRules.forEach(ruleData => {
          const rule = ruleData.rule;

          // Calcular posición y ancho basado en anticipation_slot_id y length
          // Usar el array normal de anticipation slots
          const slotIndex = this.anticipationSlots.findIndex(
            slot => slot.id === ruleData.anticipation_slot_id
          );

          const slotWidth = 100 / this.anticipationSlots.length;
          const left = slotIndex * slotWidth;
          const width = ruleData.length * slotWidth;

          const processedCard = {
            id: ruleData.id,
            ruleId: rule.id,
            strategyRuleId: ruleData.id,
            left: left,
            width: width,
            rule: rule
            //anticipationSlotId: ruleData.anticipation_slot_id
          };

          processedCards.push(processedCard);
        });

        // Crear la fila procesada
        const processedRow = {
          id: strategyRow.id,
          label: this.$t("grid_rule_set", {
            position: strategyRow.position + 1
          }),
          cards: processedCards
        };

        processedRows.push(processedRow);
      });

      return processedRows;
    },
    ...mapState({
      rule_to_place: state => state.pricingstrategy.rule_to_place
    }),
    ...mapGetters({
      getPricingStrategyRuleById: "getPricingStrategyRuleById"
    })
  },
  methods: {
    // Métodos principales de inicialización
    init() {
      this.gridRows = [...this.processedStrategyData];
      this.attachEventListeners();
      this.forceSnapAll();
    },
    attachEventListeners() {
      document.addEventListener("mousemove", this.handleMouseMove);
      document.addEventListener("mouseup", this.handleMouseUp);
      document.addEventListener("click", this.handleDocumentClick);
      document.addEventListener("keydown", this.handleKeydown);
    },
    forceSnapAll() {
      this.gridRows.forEach(row => {
        row.cards.forEach(card => {
          card.left = this.snapToGridPosition(card.left);
          card.width = this.snapToGridSize(card.width);
        });
      });
    },

    // Handles ------------------------------------------
    handleMouseMove(e) {
      if (!this.currentCard) return;
      if (this.isDragging) {
        this.handleDragMove(e);
      } else if (this.isResizing) {
        this.handleResizeMove(e);
      }
    },
    handleDragMove(e) {
      if (this.isDragging) {
        const deltaX = e.clientX - this.dragStartPosition.x;
        const deltaY = e.clientY - this.dragStartPosition.y;

        const cardId = this.parseCardId(this.currentCard.dataset.cardId);
        this.dragTransforms.set(
          cardId,
          `translate(${deltaX}px, ${deltaY}px) rotate(3deg)`
        );
      }

      const hoveredRow = this.getRowFromMousePosition(e.clientY);

      if (hoveredRow) {
        const container = hoveredRow.querySelector(".cards-container");
        const containerRect = container.getBoundingClientRect();

        const cardId = this.parseCardId(this.currentCard.dataset.cardId);
        const rowId = parseInt(this.currentRow.dataset.row);
        const sourceRow = this.gridRows.find(r => r.id === rowId);
        const targetCard =
          sourceRow && sourceRow.cards.find(c => c.id === cardId);

        if (!targetCard) return;

        let newLeft =
          ((e.clientX - this.dragOffset.x - containerRect.left) /
            containerRect.width) *
          100;
        const maxLeft = 100 - targetCard.width;
        newLeft = Math.max(0, Math.min(newLeft, maxLeft));
        newLeft = this.snapToGridPosition(newLeft);
        newLeft = Math.min(newLeft, maxLeft);

        this.updateDropPlaceholder(hoveredRow, newLeft);
      } else {
        this.hidePlaceholder();
      }

      this.highlightValidRows(hoveredRow);
    },
    handleResizeMove(e) {
      if (!this.currentCard) return;

      const container = this.currentCard.closest(".cards-container");
      const containerRect = container.getBoundingClientRect();

      const deltaX = e.clientX - this.resizeStartX;
      const deltaPercentage = (deltaX / containerRect.width) * 100;

      const cardId = this.parseCardId(this.currentCard.dataset.cardId);
      const rowId = parseInt(this.currentRow.dataset.row);
      const targetRow = this.gridRows.find(r => r.id === rowId);
      const targetCard =
        targetRow && targetRow.cards.find(c => c.id === cardId);

      if (!targetCard) return;

      const minWidth = 100 / this.gridColumns;

      let newLeft = targetCard.left;
      let newWidth = targetCard.width;

      if (this.resizeSide === "right") {
        newWidth = this.resizeStartWidth + deltaPercentage;
        newWidth = Math.max(newWidth, minWidth);

        if (newLeft + newWidth > 100) {
          newWidth = 100 - newLeft;
        }
      } else if (this.resizeSide === "left") {
        newLeft = this.resizeStartLeft + deltaPercentage;
        newWidth = this.resizeStartWidth - deltaPercentage;

        if (newLeft < 0) {
          newWidth += newLeft;
          newLeft = 0;
        }

        newWidth = Math.max(newWidth, minWidth);
      }

      newLeft = this.snapToGridPosition(newLeft);
      newWidth = this.snapToGridSize(newWidth);

      if (newLeft + newWidth > 100) {
        newWidth = 100 - newLeft;
        newWidth = this.snapToGridSize(newWidth);
      }

      newWidth = Math.max(newWidth, minWidth);

      const otherCards = targetRow.cards.filter(c => c.id !== cardId);
      const wouldCollide = otherCards.some(card => {
        const cardEnd = card.left + card.width;
        const newEnd = newLeft + newWidth;
        return !(cardEnd <= newLeft || card.left >= newEnd);
      });

      if (wouldCollide) {
        if (this.resizeSide === "right") {
          const rightLimit = Math.min(
            ...otherCards
              .filter(card => card.left >= targetCard.left)
              .map(card => card.left)
          );

          if (rightLimit !== Infinity) {
            newWidth = Math.min(newWidth, rightLimit - newLeft);
            newWidth = Math.max(newWidth, minWidth);
          }
        } else if (this.resizeSide === "left") {
          const leftLimit = Math.max(
            ...otherCards
              .filter(
                card =>
                  card.left + card.width <= targetCard.left + targetCard.width
              )
              .map(card => card.left + card.width)
          );

          if (leftLimit !== -Infinity) {
            const maxLeft = leftLimit;
            const minLeft = Math.max(0, maxLeft);
            newLeft = Math.max(newLeft, minLeft);
            newWidth = targetCard.left + targetCard.width - newLeft;
            newWidth = Math.max(newWidth, minWidth);
          }
        }
      }

      const columnWidth = 100 / this.gridColumns;
      const startColumnIndex = Math.floor(newLeft / columnWidth);
      const endColumnIndex = Math.floor(
        (newLeft + newWidth - 0.01) / columnWidth
      );

      const columnsToHighlight = [];
      for (
        let i = startColumnIndex;
        i <= Math.min(endColumnIndex, this.gridColumns - 1);
        i++
      ) {
        if (i >= 0 && i < this.gridColumns) {
          columnsToHighlight.push(i);
        }
      }

      this.highlightedColumnIndices = columnsToHighlight;

      targetCard.left = newLeft;
      targetCard.width = newWidth;
    },
    handleMouseUp(e) {
      if (!this.currentCard) return;
      if (this.isDragging) {
        this.finishDrag(e);
      } else if (this.isResizing) {
        this.finishResize();
      }
      this.cleanup();
    },
    finishDrag(e) {
      const hoveredRow = this.getRowFromMousePosition(e.clientY);

      this.currentCard.style.transform = "";
      this.currentCard.style.zIndex = "";
      this.currentCard.style.pointerEvents = "";
      this.currentCard.classList.remove("dragging");

      const cardId = this.parseCardId(this.currentCard.dataset.cardId);
      const currentRowId = parseInt(this.currentRow.dataset.row);
      const currentRowData = this.gridRows.find(r => r.id === currentRowId);
      const cardData =
        currentRowData && currentRowData.cards.find(c => c.id === cardId);

      if (!cardData) return;

      let moveSuccessful = false;
      const originalRowId = currentRowId;

      const placeholderWasVisible =
        this.$refs.dropPlaceholder &&
        this.$refs.dropPlaceholder.classList.contains("visible");

      if (hoveredRow && placeholderWasVisible) {
        const container = hoveredRow.querySelector(".cards-container");
        const containerRect = container.getBoundingClientRect();

        let newLeft =
          ((e.clientX - this.dragOffset.x - containerRect.left) /
            containerRect.width) *
          100;

        const maxLeft = 100 - cardData.width;
        newLeft = Math.max(0, Math.min(newLeft, maxLeft));
        newLeft = this.snapToGridPosition(newLeft);
        newLeft = Math.min(newLeft, maxLeft);

        const targetRowId = parseInt(hoveredRow.dataset.row);
        const targetRowData = this.gridRows.find(r => r.id === targetRowId);

        if (targetRowData) {
          if (hoveredRow === this.currentRow) {
            const cardsToCheck = targetRowData.cards.filter(
              c => c.id !== cardId
            );
            if (this.hasSpaceInRowData(cardsToCheck, newLeft, cardData.width)) {
              cardData.left = newLeft;
              moveSuccessful = true;
            } else {
              const availableSpace = this.findNextAvailableSpaceInRow(
                cardsToCheck,
                cardData.width
              );
              if (availableSpace !== null) {
                cardData.left = availableSpace;
                moveSuccessful = true;
              }
            }
          } else {
            if (
              this.hasSpaceInRowData(
                targetRowData.cards,
                newLeft,
                cardData.width
              )
            ) {
              this.moveCardBetweenRows(
                cardData,
                currentRowData,
                targetRowData,
                newLeft
              );
              moveSuccessful = true;
            } else {
              const availableSpace = this.findNextAvailableSpaceInRow(
                targetRowData.cards,
                cardData.width
              );
              if (availableSpace !== null) {
                this.moveCardBetweenRows(
                  cardData,
                  currentRowData,
                  targetRowData,
                  availableSpace
                );
                moveSuccessful = true;
              }
            }
          }
        }
      }

      if (!moveSuccessful) {
        cardData.left = this.originalPosition.left;
        cardData.width = this.originalPosition.width;
      } else {
        const targetRowId = hoveredRow
          ? parseInt(hoveredRow.dataset.row)
          : originalRowId;
        this.$emit("update", this.createRuleEmitData(cardData, targetRowId));
      }
    },
    finishResize() {
      this.resizingCardId = null;

      const cardId = this.parseCardId(this.currentCard.dataset.cardId);
      const rowId = parseInt(this.currentRow.dataset.row);
      const targetRow = this.gridRows.find(r => r.id === rowId);
      const targetCard =
        targetRow && targetRow.cards.find(c => c.id === cardId);

      if (targetCard) {
        this.$emit("update", this.createRuleEmitData(targetCard, rowId));
      }

      this.highlightedColumnIndices = [];
    },
    handleDocumentClick(e) {
      if (!e.target.closest(".card-menu")) {
        this.activeMenu = null;
      }

      if (
        this.isSelectingCell &&
        !e.target.closest(".available-cell") &&
        !e.target.closest(".v-menu__content") &&
        !e.target.closest(".v-list") &&
        !e.target.closest(".card-menu") &&
        !e.target.closest(".v-btn")
      ) {
        // console.log("🚫 Cancelando selección de celdas por click fuera");
        this.handleRuleSelectionCancelled();
      }
    },
    handleKeydown(e) {
      if (e.key === "Escape" && this.isSelectingCell) {
        // console.log("🚫 Cancelando selección de celdas con Escape");
        this.handleRuleSelectionCancelled();
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key === "c") {
        if (this.selectedCardForCopy) {
          this.handleCopyCard();
          e.preventDefault();
        }
        return;
      }

      if ((e.ctrlKey || e.metaKey) && e.key === "v") {
        if (this.copiedCard && !this.isSelectingCell) {
          this.handlePasteCard();
          e.preventDefault();
        }
        return;
      }
    },

    // Auxiliary functions ------------------------------------------
    snapToGridPosition(position) {
      const gridSize = 100 / this.gridColumns;
      const snapped = Math.round(position / gridSize) * gridSize;

      // Asegurar que no exceda los límites
      return Math.max(0, Math.min(snapped, 100));
    },
    snapToGridSize(size) {
      const gridSize = 100 / this.gridColumns;
      const minSize = gridSize; // Mínimo 1 slot
      const snapped = Math.round(size / gridSize) * gridSize;

      // Asegurar que tenga al menos el tamaño mínimo
      return Math.max(minSize, snapped);
    },
    getRowFromMousePosition(mouseY) {
      const rows = Array.from(document.querySelectorAll(".grid-row"));
      for (const row of rows) {
        const rect = row.getBoundingClientRect();
        if (mouseY >= rect.top && mouseY <= rect.bottom) {
          return row;
        }
      }
      return null;
    },

    highlightValidRows(hoveredRow) {
      this.hoveredRowId = null;

      if (hoveredRow) {
        const rowId = parseInt(hoveredRow.dataset.row);
        this.hoveredRowId = rowId;
      }
    },

    updateDropPlaceholder(row, left) {
      if (!this.$refs.dropPlaceholder || !this.currentCard || !row) return;

      if (
        !this.currentCard.dataset ||
        !this.currentCard.dataset.cardId ||
        !this.currentRow ||
        !this.currentRow.dataset ||
        !this.currentRow.dataset.row
      )
        return;

      const cardId = this.parseCardId(this.currentCard.dataset.cardId);
      const rowId = parseInt(this.currentRow.dataset.row);
      const sourceRow = this.gridRows.find(r => r.id === rowId);
      const targetCard =
        sourceRow && sourceRow.cards.find(c => c.id === cardId);

      if (!targetCard) return;

      const container = row.querySelector(".cards-container");
      const containerRect = container.getBoundingClientRect();

      const targetRowId = parseInt(row.dataset.row);
      const targetRowData = this.gridRows.find(r => r.id === targetRowId);

      if (targetRowData) {
        const cardsToCheck =
          row === this.currentRow
            ? targetRowData.cards.filter(c => c.id !== cardId)
            : targetRowData.cards;

        // Snap la posición para consistencia
        left = this.snapToGridPosition(left);

        if (left + targetCard.width > 100.01) {
          this.hidePlaceholder();
          return;
        }

        if (!this.hasSpaceInRowData(cardsToCheck, left, targetCard.width)) {
          this.hidePlaceholder();
          return;
        }

        const columnWidth = 100 / this.gridColumns;
        const startColumnIndex = Math.floor(left / columnWidth);
        const endColumnIndex = Math.floor(
          (left + targetCard.width - 0.01) / columnWidth
        );

        const columnsToHighlight = [];
        for (
          let i = startColumnIndex;
          i <= Math.min(endColumnIndex, this.gridColumns - 1);
          i++
        ) {
          if (i >= 0 && i < this.gridColumns) {
            columnsToHighlight.push(i);
          }
        }

        this.highlightedColumnIndices = columnsToHighlight;

        this.$refs.dropPlaceholder.style.backgroundColor =
          "rgba(52, 152, 219, 0.2)";
        this.$refs.dropPlaceholder.style.borderColor = "#3498db";
      }

      const placeholderLeft =
        containerRect.left + (left * containerRect.width) / 100;
      const placeholderTop = containerRect.top + 10;
      const placeholderWidth = (targetCard.width * containerRect.width) / 100;
      const placeholderHeight = containerRect.height - 20;

      this.$refs.dropPlaceholder.style.left = placeholderLeft + "px";
      this.$refs.dropPlaceholder.style.top = placeholderTop + "px";
      this.$refs.dropPlaceholder.style.width = placeholderWidth + "px";
      this.$refs.dropPlaceholder.style.height = placeholderHeight + "px";
      this.$refs.dropPlaceholder.classList.add("visible");
    },

    hidePlaceholder() {
      if (this.$refs.dropPlaceholder) {
        this.$refs.dropPlaceholder.classList.remove("visible");
      }
      this.highlightedColumnIndices = [];
    },

    cleanup() {
      this.isDragging = false;
      this.isResizing = false;
      this.currentCard = null;
      this.currentRow = null;
      this.dragOffset = { x: 0, y: 0 };
      this.originalPosition = { left: 0, width: 0 };
      this.highlightedColumnIndices = [];

      this.hoveredRowId = null;
      this.draggingCardId = null;
      this.resizingCardId = null;
      this.dragTransforms.clear();

      this.hidePlaceholder();

      document.querySelectorAll(".grid-card").forEach(card => {
        card.style.zIndex = "";
        card.style.pointerEvents = "";
      });
    },

    // Funciones de utilidad para colisiones y espacios
    hasSpaceInRowData(cards, left, width) {
      if (left + width > 100.01) {
        return false;
      }

      if (left < -0.01) {
        return false;
      }

      const endPosition = left + width;
      const tolerance = 0.01;

      return !cards.some(card => {
        const cardStart = card.left;
        const cardEnd = card.left + card.width;

        // Verificar overlap con tolerancia
        const hasOverlap = !(
          cardEnd <= left + tolerance || cardStart >= endPosition - tolerance
        );

        return hasOverlap;
      });
    },

    findNextAvailableSpaceInRow(cards, requiredWidth) {
      const gridSize = 100 / this.gridColumns;
      const tolerance = 0.01;

      for (
        let position = 0;
        position <= 100 - requiredWidth + tolerance;
        position += gridSize
      ) {
        // Snap la posición al grid
        position = this.snapToGridPosition(position);

        if (position + requiredWidth > 100 + tolerance) {
          break;
        }

        const hasCollision = cards.some(card => {
          const cardStart = card.left;
          const cardEnd = card.left + card.width;
          const positionEnd = position + requiredWidth;

          // Verificar overlap con tolerancia
          const hasOverlap = !(
            cardEnd <= position + tolerance ||
            cardStart >= positionEnd - tolerance
          );

          return hasOverlap;
        });

        if (!hasCollision) {
          return position;
        }
      }

      return null;
    },

    moveCardBetweenRows(cardData, fromRow, toRow, newLeft) {
      const cardIndex = fromRow.cards.findIndex(c => c.id === cardData.id);
      if (cardIndex !== -1) {
        fromRow.cards.splice(cardIndex, 1);
      }

      cardData.left = newLeft;
      toRow.cards.push(cardData);
    },

    // Métodos de controles
    addNewRow() {
      // Verificar si la última fila está vacía
      if (this.gridRows.length > 0) {
        const lastRow = this.gridRows[this.gridRows.length - 1];
        if (lastRow.cards.length === 0) {
          // console.warn(
          //   "⚠️ No se puede crear una nueva fila: la última fila está vacía"
          // );
          this.$store.dispatch("setSnackbar", {
            title: this.$t("grid_notification_no_empty_last_row"),
            color: "error"
          });
          return;
        }
      }

      const newRowPosition = this.gridRows.length;
      const defaultLabel = this.$t("grid_rule_set", {
        position: newRowPosition + 1
      });

      const newRow = {
        id: this.tempRowCounter--,
        label: defaultLabel,
        cards: []
      };

      this.gridRows.push(newRow);
      this.componentKey++;

      // console.log("➕ Nueva fila creada con ID temporal:", newRow.id);
    },

    // Event handlers para cards
    startCardDrag(e) {
      const cardElement = e.target.closest(".grid-card");
      if (!cardElement) return;

      this.currentCard = cardElement;
      this.currentRow = cardElement.closest(".grid-row");

      const cardId = this.parseCardId(this.currentCard.dataset.cardId);
      const rowId = parseInt(this.currentRow.dataset.row);
      const targetRow = this.gridRows.find(r => r.id === rowId);
      const targetCard =
        targetRow && targetRow.cards.find(c => c.id === cardId);

      if (!targetCard) return;

      this.originalPosition = {
        left: targetCard.left,
        width: targetCard.width
      };

      this.isDragging = true;
      this.draggingCardId = cardId;

      const cardRect = cardElement.getBoundingClientRect();

      this.dragStartPosition = {
        x: e.clientX,
        y: e.clientY
      };

      this.dragOffset = {
        x: e.clientX - cardRect.left,
        y: e.clientY - cardRect.top
      };

      cardElement.style.zIndex = "1000";
      cardElement.style.pointerEvents = "none";

      e.preventDefault();
    },

    startResizeLeft(e, card) {
      const cardElement = e.target.closest(".grid-card");
      if (!cardElement) return;

      if (!this.canResizeLeft(card, this.getRowByCardId(card.id))) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      this.currentCard = cardElement;
      this.currentRow = cardElement.closest(".grid-row");

      const cardId = this.parseCardId(this.currentCard.dataset.cardId);
      const rowId = parseInt(this.currentRow.dataset.row);
      const targetRow = this.gridRows.find(r => r.id === rowId);
      const targetCard =
        targetRow && targetRow.cards.find(c => c.id === cardId);

      if (!targetCard) return;

      this.originalPosition = {
        left: targetCard.left,
        width: targetCard.width
      };

      this.isResizing = true;
      this.resizingCardId = cardId;

      this.resizeStartX = e.clientX;
      this.resizeStartWidth = targetCard.width;
      this.resizeStartLeft = targetCard.left;
      this.resizeSide = "left";

      e.preventDefault();
      e.stopPropagation();
    },

    startResizeRight(e, card) {
      const cardElement = e.target.closest(".grid-card");
      if (!cardElement) return;

      if (!this.canResizeRight(card, this.getRowByCardId(card.id))) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      this.currentCard = cardElement;
      this.currentRow = cardElement.closest(".grid-row");

      const cardId = this.parseCardId(this.currentCard.dataset.cardId);
      const rowId = parseInt(this.currentRow.dataset.row);
      const targetRow = this.gridRows.find(r => r.id === rowId);
      const targetCard =
        targetRow && targetRow.cards.find(c => c.id === cardId);

      if (!targetCard) return;

      this.originalPosition = {
        left: targetCard.left,
        width: targetCard.width
      };

      this.isResizing = true;
      this.resizingCardId = cardId;

      this.resizeStartX = e.clientX;
      this.resizeStartWidth = targetCard.width;
      this.resizeStartLeft = targetCard.left;
      this.resizeSide = "right";

      e.preventDefault();
      e.stopPropagation();
    },

    toggleCardMenu(cardId, rowId) {
      const menuKey = `${cardId}-${rowId}`;
      this.activeMenu = this.activeMenu === menuKey ? null : menuKey;
    },

    copyCard(card) {
      this.copiedCard = {
        id: `copy-${this.copyCardCounter++}`,
        ruleId: card.ruleId || card.id,
        strategyRuleId: null,
        rule: card.rule,
        isCopied: true,
        originalCard: card
      };

      // console.log("📋 Regla copiada:", this.copiedCard);

      this.selectedRuleToAdd = this.copiedCard;
      this.isSelectingCell = true;
      this.rangeSelection = {
        isSelectingRange: false,
        startCell: null,
        endCell: null,
        targetRowId: null,
        hoveredCell: null
      };
      this.calculateAvailableCells();

      this.showCopyNotification();
      this.activeMenu = null;

      // console.log(
      //   "🎯 Modo selección activado - Haz clic en una celda azul para pegar"
      // );
    },

    editCard(cardId) {
      // El modo es compartido con los guardarailes: hay que devolverlo a
      // "rule" o el canvas se abriría con el catálogo de condiciones filtrado
      this.$store.dispatch("setPricingStrategyRuleDefinitionMode", "rule");
      this.rule_to_edit = this.getPricingStrategyRuleById(cardId);
      this.rule_definition_dialog = true;
      this.activeMenu = null;
    },

    deleteCard(cardId, rowId) {
      const targetRow = this.gridRows.find(row => row.id === rowId);
      if (!targetRow) return;

      const cardIndex = targetRow.cards.findIndex(card => card.id === cardId);
      if (cardIndex !== -1) {
        const deletedCard = targetRow.cards[cardIndex];
        targetRow.cards.splice(cardIndex, 1);

        this.$emit("update", {
          delete: true,
          strategy_id: this.strategyRulesData.id,
          strategy_rule_id: deletedCard.strategyRuleId || null
        });
      }
      this.activeMenu = null;
    },

    // Funciones auxiliares para calcular slot y length
    calculateAnticipationSlotId(leftPercentage) {
      const slotWidth = 100 / this.anticipationSlots.length;
      const slotIndex = Math.round(leftPercentage / slotWidth);

      // Usar el array normal de anticipation slots
      const slot = this.anticipationSlots[slotIndex];
      return slot
        ? slot.id
        : this.anticipationSlots[0] && this.anticipationSlots[0].id;
    },

    calculateLength(widthPercentage) {
      const slotWidth = 100 / this.anticipationSlots.length;
      return Math.max(1, Math.round(widthPercentage / slotWidth));
    },

    createRuleEmitData(card, rowId, newLeft = null, newWidth = null) {
      const left = newLeft !== null ? newLeft : card.left;
      const width = newWidth !== null ? newWidth : card.width;

      // console.log("🔍 createRuleEmitData - card:", {
      //   id: card.id,
      //   ruleId: card.ruleId,
      //   strategyRuleId: card.strategyRuleId,
      //   isNewCard: typeof card.id === "string"
      // });

      return {
        strategy_id: this.strategyRulesData.id,
        row_id: rowId < 0 ? null : rowId,
        rule_id: card.ruleId || card.id,
        strategy_rule_id: card.strategyRuleId || null,
        anticipation_slot_id: this.calculateAnticipationSlotId(left),
        length: this.calculateLength(width),
        // Añadir información para identificar la card temporal si es necesario
        temp_card_id: card.strategyRuleId ? null : card.id
      };
    },

    // Funciones para detectar si se puede redimensionar
    canResizeLeft(card, row) {
      const minWidth = 100 / this.gridColumns;

      if (card.width > minWidth) {
        return true;
      }

      if (card.left <= 0) return false;

      const otherCards = row.cards.filter(c => c.id !== card.id);
      const leftBlocker = otherCards.find(otherCard => {
        return (
          otherCard.left + otherCard.width >= card.left &&
          otherCard.left < card.left
        );
      });

      if (leftBlocker) {
        const availableSpace =
          card.left - (leftBlocker.left + leftBlocker.width);
        return availableSpace > 0;
      }

      return true;
    },

    canResizeRight(card, row) {
      const minWidth = 100 / this.gridColumns;

      if (card.width > minWidth) {
        return true;
      }

      if (card.left + card.width >= 100) return false;

      const otherCards = row.cards.filter(c => c.id !== card.id);
      const rightBlocker = otherCards.find(otherCard => {
        return (
          otherCard.left <= card.left + card.width && otherCard.left > card.left
        );
      });

      if (rightBlocker) {
        const availableSpace = rightBlocker.left - (card.left + card.width);
        return availableSpace > 0;
      }

      return true;
    },

    openTemplatesDialog() {
      this.$store.dispatch("setPricingStrategyRuleTemplatesKind", "rule");
      this.$store.dispatch("setPricingStrategyRuleDefinitionMode", "rule");
      this.$store.dispatch("setPricingStrategyRuleTemplatesDialog", true);
    },

    // Métodos para selección de reglas y celdas
    handleRuleSelected(rule) {
      rule.rule = rule;
      this.selectedRuleToAdd = rule;
      this.isSelectingCell = true;
      this.rangeSelection = {
        isSelectingRange: false,
        startCell: null,
        endCell: null,
        targetRowId: null,
        hoveredCell: null
      };
      this.calculateAvailableCells();
    },

    handleRuleSelectionCancelled() {
      this.selectedRuleToAdd = null;
      this.isSelectingCell = false;
      this.availableCells = [];
      this.rangeSelection = {
        isSelectingRange: false,
        startCell: null,
        endCell: null,
        targetRowId: null,
        hoveredCell: null
      };

      this.selectedCardForCopy = null;
      this.clearCardSelection();
    },

    handleCellClick(cell, rowId) {
      if (this.selectedRuleToAdd && this.selectedRuleToAdd.isCopied) {
        const targetRow = this.gridRows.find(r => r.id === rowId);
        if (!targetRow) return;

        const newCard = this.createCardFromRule(
          this.selectedRuleToAdd,
          cell.left
        );

        if (
          this.selectedRuleToAdd.originalCard &&
          this.selectedRuleToAdd.originalCard.width
        ) {
          newCard.width = this.selectedRuleToAdd.originalCard.width;
        }

        targetRow.cards.push(newCard);

        this.$emit("update", this.createRuleEmitData(newCard, rowId));

        // console.log("🎉 Regla pegada:", {
        //   left: cell.left,
        //   width: newCard.width
        // });

        this.handleRuleSelectionCancelled();

        return;
      }

      if (!this.rangeSelection.isSelectingRange) {
        this.rangeSelection.startCell = { ...cell, rowId };
        this.rangeSelection.targetRowId = rowId;
        this.rangeSelection.isSelectingRange = true;
        // console.log("🎯 Inicio seleccionado:", cell.left);
      } else {
        if (rowId !== this.rangeSelection.targetRowId) {
          // console.warn("⚠️ No se puede seleccionar en una fila diferente");
          return;
        }

        this.rangeSelection.endCell = { ...cell, rowId };
        // console.log("🎯 Fin seleccionado:", cell.left);

        this.createRuleFromRange();
      }
    },

    handleCellHover(cell, rowId) {
      if (
        this.rangeSelection.isSelectingRange &&
        rowId === this.rangeSelection.targetRowId
      ) {
        this.rangeSelection.hoveredCell = { ...cell, rowId };
      }
    },

    handleCellLeave() {
      // No limpiar hoveredCell aquí para mantener el rango visible
    },

    isInDynamicRange(cell, rowId) {
      if (
        !this.rangeSelection.startCell ||
        !this.rangeSelection.isSelectingRange
      )
        return false;
      if (rowId !== this.rangeSelection.targetRowId) return false;
      if (!this.rangeSelection.hoveredCell) return false;

      const start = this.rangeSelection.startCell.left;
      const end = this.rangeSelection.hoveredCell.left;

      const minPos = Math.min(start, end);
      const maxPos = Math.max(start, end);

      return cell.left > minPos && cell.left < maxPos;
    },

    createRuleFromRange() {
      if (
        !this.rangeSelection.startCell ||
        !this.rangeSelection.hoveredCell ||
        !this.selectedRuleToAdd
      ) {
        // console.error("❌ Selección incompleta");
        return;
      }

      const startLeft = Math.min(
        this.rangeSelection.startCell.left,
        this.rangeSelection.hoveredCell.left
      );
      const endLeft = Math.max(
        this.rangeSelection.startCell.left,
        this.rangeSelection.hoveredCell.left
      );
      const width = endLeft - startLeft + this.rangeSelection.hoveredCell.width;

      const targetRow = this.gridRows.find(
        r => r.id === this.rangeSelection.targetRowId
      );
      if (!targetRow) return;

      const newCard = this.createCardFromRule(
        this.selectedRuleToAdd,
        startLeft
      );
      newCard.width = width;

      targetRow.cards.push(newCard);

      this.$emit(
        "update",
        this.createRuleEmitData(newCard, this.rangeSelection.targetRowId)
      );

      // console.log("🎉 Regla añadida con rango:", {
      //   left: startLeft,
      //   width: width
      // });

      this.handleRuleSelectionCancelled();
    },

    calculateAvailableCells() {
      if (!this.selectedRuleToAdd) return;

      this.availableCells = [];

      let requiredWidth;

      if (
        this.selectedRuleToAdd.isCopied &&
        this.selectedRuleToAdd.originalCard &&
        this.selectedRuleToAdd.originalCard.width
      ) {
        requiredWidth = this.selectedRuleToAdd.originalCard.width;
      } else {
        requiredWidth = 100 / this.gridColumns;
      }

      // console.log("📏 Ancho requerido para la regla:", requiredWidth);

      this.gridRows.forEach(row => {
        const rowCells = this.findAvailableCellsInRow(row.cards, requiredWidth);
        rowCells.forEach(cell => {
          this.availableCells.push({
            rowId: row.id,
            left: cell.left,
            width: cell.width
          });
        });
      });

      // console.log(
      //   "📊 Celdas disponibles calculadas:",
      //   this.availableCells.length
      // );
    },

    findAvailableCellsInRow(cards, requiredWidth) {
      const gridSize = 100 / this.gridColumns;
      const availableCells = [];
      const tolerance = 0.01;

      for (
        let position = 0;
        position <= 100 - requiredWidth + tolerance;
        position += gridSize
      ) {
        // Snap la posición al grid para consistencia
        position = this.snapToGridPosition(position);

        if (position + requiredWidth > 100 + tolerance) {
          break;
        }

        const positionEnd = position + requiredWidth;

        const hasCollision = cards.some(card => {
          const cardStart = card.left;
          const cardEnd = card.left + card.width;

          const collision = !(
            cardEnd <= position + tolerance ||
            cardStart >= positionEnd - tolerance
          );

          return collision;
        });

        if (!hasCollision) {
          availableCells.push({
            left: position,
            width: requiredWidth
          });
        }
      }

      return availableCells;
    },

    getAvailableCellsForRow(rowId) {
      return this.availableCells.filter(cell => cell.rowId === rowId);
    },

    createCardFromRule(rule, left) {
      const width = (100 / this.gridColumns) * 1;

      // Determinar el ID según el tipo de regla
      let cardId;
      let ruleId;
      let ruleData;

      if (rule.isCopied) {
        // Para reglas copiadas, usar ID con prefijo copy-
        cardId = `copy-${this.copyCardCounter++}`;
        ruleId = rule.ruleId;
        ruleData = rule.rule; // Usar la regla original copiada
      } else {
        // Para reglas nuevas, usar ID con prefijo temp-
        cardId = `temp-${this.tempCardCounter++}`;
        ruleId = rule.id;
        ruleData = rule; // Para reglas nuevas, usar directamente la regla
      }

      return {
        id: cardId,
        ruleId: ruleId,
        strategyRuleId: null, // Las cards nuevas y copiadas no tienen strategyRuleId hasta que se guarden
        left: left,
        width: width,
        rule: ruleData
      };
    },

    // Funciones para copy/paste de reglas
    handleCopyCard() {
      if (!this.selectedCardForCopy) return;
      const { card } = this.selectedCardForCopy;
      this.copiedCard = {
        id: `copy-${this.copyCardCounter++}`,
        ruleId: card.ruleId || card.id,
        strategyRuleId: null,
        rule: card.rule,
        isCopied: true,
        originalCard: card
      };
      this.showCopyNotification();
      this.selectedCardForCopy = null;
      this.clearCardSelection();
    },

    handlePasteCard() {
      if (!this.copiedCard) return;

      this.selectedRuleToAdd = this.copiedCard;
      this.isSelectingCell = true;
      this.rangeSelection = {
        isSelectingRange: false,
        startCell: null,
        endCell: null,
        targetRowId: null,
        hoveredCell: null
      };
      this.calculateAvailableCells();
    },

    showCopyNotification() {
      this.$store.dispatch("setSnackbar", {
        title: this.$t("grid_notification_rule_copied"),
        color: "success"
      });
    },

    clearCardSelection() {
      document.querySelectorAll(".grid-card").forEach(card => {
        card.classList.remove("selected-for-copy");
      });
    },

    // Nuevos métodos para controles de fila
    clearRowCards(rowId) {
      const targetRow = this.gridRows.find(row => row.id === rowId);
      if (!targetRow) return;

      if (targetRow.cards.length > 0) {
        this.dialogData.targetRowId = rowId;
        this.dialogData.targetRowLabel = targetRow.label;

        this.dialogs.confirmClearRow = true;
      }
    },

    deleteRow(rowId) {
      if (this.gridRows.length <= 1) {
        // console.warn("⚠️ No se puede eliminar la última fila");
        return;
      }

      const targetRow = this.gridRows.find(row => row.id === rowId);
      if (!targetRow) return;

      this.dialogData.targetRowId = rowId;
      this.dialogData.targetRowLabel = targetRow.label;

      this.dialogs.confirmDeleteRow = true;
    },

    // Funciones auxiliares para diálogos
    getRowById(rowId) {
      return this.gridRows.find(row => row.id === rowId);
    },

    confirmClearRowCards() {
      const targetRow = this.getRowById(this.dialogData.targetRowId);
      if (!targetRow) return;
      targetRow.cards.forEach(card => {
        this.$emit("update", {
          delete: true,
          strategy_id: this.strategyRulesData.id,
          strategy_rule_id: card.strategyRuleId || null
        });
      });
      targetRow.cards = [];
      // console.log(`🧹 Fila "${targetRow.label}" vaciada de todas las reglas`);
      this.dialogs.confirmClearRow = false;
    },

    confirmDeleteRow() {
      const targetRow = this.getRowById(this.dialogData.targetRowId);
      if (!targetRow) return;

      // Solo eliminar filas existentes (no temporales)
      if (targetRow.id > 0) {
        const params = {
          strategy_id: this.strategyRulesData.id,
          row_id: targetRow.id
        };

        this.$store
          .dispatch("deletePricingStrategyStrategyRow", params)
          .then(() => {
            // console.log(`🗑️ Fila "${targetRow.label}" eliminada del backend`);
          })
          .catch(() => {
            // console.error("❌ Error al eliminar la fila del backend");
          });
      }

      // Eliminar la fila localmente
      const rowIndex = this.gridRows.findIndex(
        row => row.id === this.dialogData.targetRowId
      );
      if (rowIndex !== -1) {
        this.gridRows.splice(rowIndex, 1);
        // console.log(`🗑️ Fila "${targetRow.label}" eliminada localmente`);
      }

      this.componentKey++;
      this.dialogs.confirmDeleteRow = false;
    },

    // Método auxiliar para encontrar la fila que contiene una card específica
    getRowByCardId(cardId) {
      for (const row of this.gridRows) {
        const card = row.cards.find(c => c.id === cardId);
        if (card) {
          return row;
        }
      }
      return null;
    },

    // Helper para convertir cardId del dataset al tipo correcto
    parseCardId(cardIdString) {
      // Si es un ID temporal o copiado (contiene letras), mantenerlo como string
      if (cardIdString.includes("temp-") || cardIdString.includes("copy-")) {
        return cardIdString;
      }
      // Si es numérico, convertirlo a número
      return parseInt(cardIdString);
    },

    // Función de debug para troubleshooting
    debugSpaceAvailability(rowId, left, width) {
      const targetRow = this.gridRows.find(r => r.id === rowId);
      if (!targetRow) return;

      // console.log("🔍 Debug Space Availability:", {
      //   rowId,
      //   requestedPosition: left,
      //   requestedWidth: width,
      //   requestedEnd: left + width,
      //   existingCards: targetRow.cards.map(card => ({
      //     id: card.id,
      //     left: card.left,
      //     width: card.width,
      //     end: card.left + card.width
      //   })),
      //   gridSize: 100 / this.gridColumns,
      //   hasSpace: this.hasSpaceInRowData(targetRow.cards, left, width)
      // });
    }
  },
  watch: {
    // Watcher para actualizar gridRows cuando cambien los datos de la estrategia
    processedStrategyData: {
      handler(newData) {
        this.gridRows = [...newData];
      },
      deep: true
    },
    // Al crear una regla en el canvas, entrar directamente en modo colocación
    rule_to_place(rule) {
      if (!rule) return;
      if (!this.readOnly) {
        this.handleRuleSelected(rule);
      }
      // Limpiar siempre el estado, incluso en modo readOnly
      this.$store.dispatch("setPricingStrategyRuleToPlace", null);
    }
  },
  mounted() {
    setTimeout(() => {
      this.init();
    }, 100);
  }
};
</script>
<style lang="scss" scoped>
// Variables
$primary-color: #3498db;
$primary-dark: #2980b9;
$success-color: #4caf50;
$warning-color: #ffc107;
$error-color: #f44336;
$orange-color: #ff9800;
$blue-light: #e3f2fd;
$blue-medium: #bbdefb;
$blue-dark: #1976d2;
$blue-primary: #2196f3;
$gray-light: #f0f0f0;
$gray-medium: #f1f1f1;

.strategy-grid {
  &.read-only {
    .cards-container {
      pointer-events: none; // Deshabilitar todas las interacciones del contenedor
    }
    .grid-card {
      pointer-events: auto; // Permitir hover en las cards
    }
  }
}

.grid-row {
  display: flex;
  min-height: 135px;
  padding: 0 10px;
  border-bottom: none;
  position: relative;
  margin-bottom: 10px;
  background: $gray-medium;
  border-radius: 10px;

  &:last-child {
    border-bottom: none;
  }

  &.drag-over {
    transition: all 0.2s ease !important;

    .cards-container {
      border: none;
      border-radius: 8px !important;
    }
  }
}

.cards-container {
  flex: 1;
  position: relative;
  background: none;
  transition: all 0.3s ease;
  pointer-events: auto;
  border-radius: 8px;
  // Estados de drag over
  &.drag-target {
    background-color: rgba($primary-color, 0.15) !important;
    border-radius: 8px !important;
    transition: all 0.2s ease !important;
  }
  // Estado de selección de celdas
  &.selecting-cells {
    .grid-card {
      opacity: 0.6;
      pointer-events: none;
    }
  }
}

.drop-placeholder {
  position: fixed;
  background: rgba($primary-color, 0.2);
  border: 2px dashed $primary-color;
  border-radius: 6px;
  display: none;
  align-items: center;
  justify-content: center;
  color: $primary-color;
  font-weight: 500;
  font-size: 12px;
  z-index: 999;
  pointer-events: none;
  transition: all 0.2s ease;
  &.visible {
    display: flex;
    animation: placeholderPulse 1s ease-in-out infinite;
  }
}

@keyframes placeholderPulse {
  0%,
  100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.02);
  }
}

// Celdas disponibles para añadir reglas
.available-cell {
  position: absolute;
  top: 10px;
  bottom: 10px;
  border-radius: 8px;
  border: 1px dashed $primary-color;
  background: rgba($primary-color, 0.1);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  &:hover {
    background: rgba($primary-color, 0.2);
    border-color: $primary-dark;
    transform: scale(1.02);
  }
  &.start-selected {
    background: rgba($success-color, 0.2);
    border-color: $success-color;
    border-style: solid;
  }
  &.end-hovered {
    background: rgba($error-color, 0.2);
    border-color: $error-color;
    border-style: solid;
  }
  &.in-range {
    background: rgba($warning-color, 0.2);
    border-color: $warning-color;
    border-style: solid;
  }
}

.cell-icon {
  color: $primary-color;
}

// Mensaje de ayuda durante selección
.selection-help {
  animation: slideDown 0.3s ease-out;
  background: linear-gradient(135deg, $blue-light 0%, $blue-medium 100%);
  border-bottom: 2px solid $blue-primary;
  margin-bottom: 15px;
  position: sticky;
  top: 0;
  width: 100%;
  z-index: 100;
}

@keyframes slideDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.help-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px 20px;
  color: $blue-dark;
  font-weight: 500;

  span {
    flex: 1;
    text-align: center;
    font-size: 14px;
  }
}

// Controles de fila
.row-controls {
  gap: 4px;
  padding: 8px 0 8px 8px;
  min-width: 40px;
  transition: all 0.2s ease;
}

// Estilos para copy/paste
.grid-card {
  &.selected-for-copy {
    border: 2px solid $orange-color !important;
    box-shadow: 0 0 0 4px rgba($orange-color, 0.2) !important;
    animation: pulseOrange 2s ease-in-out infinite;
  }
}

@keyframes pulseOrange {
  0%,
  100% {
    box-shadow: 0 0 0 4px rgba($orange-color, 0.2);
  }
  50% {
    box-shadow: 0 0 0 8px rgba($orange-color, 0.4);
  }
}

// Animaciones para notificaciones
@keyframes slideInRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slideOutRight {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}
</style>
